export function getErrorTranslationKey(errorCode: number | undefined): string {
  if (!errorCode) {
    return 'common.errors.code.40000'
  }

  return `common.errors.code.${errorCode}`
}

export function extractErrorCode(error: any): number | undefined {
  if (error?.response?.data?.code) {
    return error.response.data.code
  }

  if (error?.response?.status) {
    const statusCode = error.response.status
    // Return 5-digit format: HTTP_STATUS_CODE * 100 (e.g., 400 -> 40000, 404 -> 40400, 500 -> 50000)
    if (statusCode >= 400 && statusCode < 500) {
      return statusCode * 100
    }
    if (statusCode >= 500 && statusCode < 600) {
      return statusCode * 100
    }
  }

  return undefined
}

/**
 * Returns true if the error includes a code in the response body (i.e. from our backend).
 * When false, the code was derived from HTTP status (e.g. external APIs) and we should
 * show the API's message as-is instead of translating to generic "Something went wrong".
 */
export function isErrorCodeFromResponseBody(error: any): boolean {
  return error?.response?.data?.code !== undefined && error?.response?.data?.code !== null
}

/**
 * Gets the original error message from backend response
 * This is used as fallback when translation is not available
 */
export function getBackendErrorMessage(error: any): string | string[] | Array<{ message: string; code: number; data?: any }> | undefined {
  const message = error?.response?.data?.message
  if (!message) {
    return undefined
  }

  if (Array.isArray(message)) {
    return message
  }

  return typeof message === 'string' ? message : String(message)
}

/**
 * Maps document status values to their translation keys
 * @param status - The status value (e.g., 'draft', 'completed', 'declined')
 * @returns Translation key for the status, or the original status if no translation exists
 */
function getStatusTranslationKey(status: string): string {
  const statusMap: Record<string, string> = {
    draft: 'common.draft',
    completed: 'common.completed',
    declined: 'common.declined',
    accepted: 'common.accepted',
    viewed: 'common.viewed',
    sent: 'proposals.sendLink.sent',
    expired: 'common.expired',
    waiting_for_payment: 'common.paymentPending',
    paid: 'common.paid',
    archived: 'common.archived',
  }

  return statusMap[status.toLowerCase()] || status
}

/**
 * Translates a single error message based on error code
 * @param message - The error message to translate
 * @param errorCode - The error code to use for translation
 * @param errorData - Additional error data for parameter interpolation
 * @param t - Translation function from vue-i18n
 * @param fallbackMessage - Optional fallback message
 * @param isCodeFromResponseBody - When false (external API), show API message as-is instead of generic translation
 * @returns Translated error message
 */
function translateSingleMessage(
  message: string,
  errorCode: number | undefined,
  errorData: any,
  t: (key: string, params?: Record<string, any>) => string,
  fallbackMessage?: string,
  isCodeFromResponseBody?: boolean
): string {
  // External APIs (no code in response): show their message as-is
  if (isCodeFromResponseBody === false && message?.trim()) {
    return message.trim()
  }

  const translationKey = getErrorTranslationKey(errorCode)
  const params = errorData && typeof errorData === 'object' ? { ...errorData } : {}

  if (params.message) {
    delete params.message
  }

  if (errorCode === 40007 && params.status && typeof params.status === 'string') {
    const statusTranslationKey = getStatusTranslationKey(params.status)
    if (statusTranslationKey !== params.status) {
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      const translatedStatus = t(statusTranslationKey)
      params.status = translatedStatus !== statusTranslationKey ? translatedStatus : params.status
    }
  }

  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  const translatedMessage = Object.keys(params).length > 0 ? t(translationKey, params) : t(translationKey)

  if (translatedMessage !== translationKey) {
    return Array.isArray(translatedMessage) ? translatedMessage.join(', ') : String(translatedMessage)
  }

  return message || fallbackMessage || t('common.errors.code.40000')
}

/**
 * Centralized utility to get translated error message from an axios error
 * This function handles the complete flow:
 * 1. Extract error code from response
 * 2. If message is an array, translate each message individually
 * 3. Try to get translated message (with parameters if available)
 * 4. Fallback to backend message if translation is missing
 * 5. Fallback to generic error if neither is available
 *
 * @param error - Axios error or any error object
 * @param t - Translation function from vue-i18n
 * @param fallbackMessage - Optional fallback message if translation and backend message are both missing
 * @returns The translated or fallback error message (string if single, joined string if array)
 */
export function getTranslatedErrorMessage(
  error: any,
  t: (key: string, params?: Record<string, any>) => string,
  fallbackMessage?: string
): string {
  const errorCode = extractErrorCode(error)
  const backendMessage = getBackendErrorMessage(error)
  const errorData = error?.response?.data?.data || error?.response?.data
  const isCodeFromResponseBody = isErrorCodeFromResponseBody(error)

  if (Array.isArray(backendMessage)) {
    const translatedMessages = backendMessage.map((msg: any) => {
      if (typeof msg === 'object' && msg.message && msg.code) {
        return translateSingleMessage(msg.message, msg.code, msg.data || errorData, t, fallbackMessage, true)
      }
      const messageText = typeof msg === 'string' ? msg : String(msg)
      return translateSingleMessage(messageText, errorCode, errorData, t, fallbackMessage, isCodeFromResponseBody)
    })

    return translatedMessages.join(', ')
  }

  // External API (no code in body): show message as-is without translation
  if (typeof backendMessage === 'string' && !isCodeFromResponseBody && backendMessage.trim()) {
    return backendMessage.trim()
  }

  if (typeof backendMessage === 'string') {
    return translateSingleMessage(backendMessage, errorCode, errorData, t, fallbackMessage, isCodeFromResponseBody)
  }

  return fallbackMessage || t('common.errors.code.40000')
}
